/**
 * WebRTC audio device store
 *
 * Persists selected audio input (mic) and output (speaker) device IDs
 * in localStorage for use when answering/placing calls and for remote audio sink.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================================================
// Types
// ============================================================================

interface WebrtcAudioDeviceState {
  /** Selected microphone deviceId (empty = default) */
  audioInputDeviceId: string;
  /** Selected speaker deviceId (empty = default; only used when setSinkId is supported) */
  audioOutputDeviceId: string;
}

interface WebrtcAudioDeviceActions {
  setAudioInputDeviceId: (deviceId: string) => void;
  setAudioOutputDeviceId: (deviceId: string) => void;
}

type WebrtcAudioDeviceStore = WebrtcAudioDeviceState & WebrtcAudioDeviceActions;

const STORAGE_KEY = 'autocollect-webrtc-audio-devices';

// ============================================================================
// Store
// ============================================================================

export const useWebrtcAudioDeviceStore = create<WebrtcAudioDeviceStore>()(
  persist(
    set => ({
      audioInputDeviceId: '',
      audioOutputDeviceId: '',

      setAudioInputDeviceId: deviceId => set({ audioInputDeviceId: deviceId ?? '' }),
      setAudioOutputDeviceId: deviceId => set({ audioOutputDeviceId: deviceId ?? '' }),
    }),
    {
      name: STORAGE_KEY,
      partialize: state => ({
        audioInputDeviceId: state.audioInputDeviceId,
        audioOutputDeviceId: state.audioOutputDeviceId,
      }),
    }
  )
);
